/* ==========================================================================
   estateContext

   Every estate_admin and every regular user is locked to their own
   estate — that's what sessionStore.user.estateId already gives us, and
   nothing here changes that for them.

   A super_admin is different: they don't belong to just one estate, they
   oversee all of them. This module is the one place that tracks WHICH
   estate a super_admin is currently looking at — defaults to their own
   home estate, but can be switched (see ui/estates.ts, the "Estates"
   screen in Hub -> Platform).

   Every service that reads or writes estate-scoped data (wallet, meters,
   bills, transactions) reads `estateContext.activeEstateId` instead of
   reaching into sessionStore.user.estateId directly, so switching estates
   as a super_admin actually changes what the whole app shows — without
   ever needing a second login or a second deployment.
   ========================================================================== */

import { sessionStore } from "./session";

let overrideEstateId: string | null = null;
const listeners = new Set<(estateId: string | null) => void>();

function notify(): void {
  const current = estateContext.activeEstateId;
  listeners.forEach((fn) => fn(current));
}

export const estateContext = {
  /** The estate every estate-scoped read/write should use right now. Null
   *  only when nobody is signed in. */
  get activeEstateId(): string | null {
    return overrideEstateId ?? sessionStore.user?.estateId ?? null;
  },

  /** True when a super_admin has switched away from their own home
   *  estate — useful for showing "you're viewing X, not your own estate"
   *  context in the UI so it's never ambiguous which estate is on screen. */
  get isOverridden(): boolean {
    return overrideEstateId !== null && overrideEstateId !== sessionStore.user?.estateId;
  },

  setActiveEstate(estateId: string | null): void {
    overrideEstateId = estateId;
    notify();
  },

  /** Back to the super_admin's own home estate. */
  resetToOwnEstate(): void {
    overrideEstateId = null;
    notify();
  },

  subscribe(fn: (estateId: string | null) => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

/** Same as estateContext.activeEstateId, but throws instead of returning
 *  null — for the many call sites (services) that can't sensibly proceed
 *  without an estate and would rather fail loudly than silently query
 *  with an empty id. */
export function requireActiveEstateId(): string {
  const id = estateContext.activeEstateId;
  if (!id) throw new Error("No active estate — are you signed in?");
  return id;
}
